import { PageEvent } from '../controllers/transition.controller';
import { Component, ComponentEvent } from '../components/Component';
import { toKebabCase } from '../utils/strings.util';
import { addBlockId, BlockEvent, blockLifecycleController, type BlockLifecycleInfo } from '../controllers/block-lifecycle.controller';
import rosController from '../controllers/ros.controller';

const isIframeEditor = (): boolean => {
	const body = document.querySelector('body');
	if (!body) return false;
	return body.classList.contains('block-editor-iframe__body');
};
const LOG_PREFIX: string = isIframeEditor() ? '[module-loader-iframe]' : '[module-loader]';
const DEBUG_VERBOSE = false;
const ENABLE_COMPONENT_AUTOLOADING = false;

const prefix = 'hhds';
const blocktypeQuery: string = '[hhds-blocktype]';

DEBUG_VERBOSE && console.log(LOG_PREFIX, 'loaded');

const blockModules = Object.entries(import.meta.glob('../blocks/{core,feature}/*/*.ts', { eager: false }))
	.filter(([path]) => {
		const match = path.match(/\/([^/]+)\/\1\.ts$/); // Matches /[dirname]/[dirname].ts
		return !!match;
	})
	.reduce(
		(acc, [path, mod]) => {
			acc[path] = mod;
			return acc;
		},
		{} as Record<string, any>
	);

const componentModules = ENABLE_COMPONENT_AUTOLOADING
	? Object.entries(import.meta.glob('../components/*/*.ts', { eager: false }))
			.filter(([path]) => {
				const match = path.match(/\/([^/]+)\/\1\.ts$/);
				return !!match;
			})
			.reduce(
				(acc, [path, mod]) => {
					acc[path] = mod;
					return acc;
				},
				{} as Record<string, any>
			)
	: {};

const modules = { ...componentModules, ...blockModules };

const tagToLoaderMap: Record<string, () => Promise<any>> = {};

for (const path in modules) {
	let match = path.match(/\.{1,2}\/components\/([^/]+)\/\1\.ts$/);
	if (match) {
		const kebabName = toKebabCase(match[1]);
		const tag = `${prefix}-${kebabName}`;
		tagToLoaderMap[tag] = modules[path];
		//DEBUG_VERBOSE && console.log(LOG_PREFIX, modules[path]);
	}

	// For blocks in either blocks directory, map as follows:
	// hhds-blocktype-cta to ../blocks/core/CTA/CTA.ts
	// hhds-blocktype-carousel to ../blocks/core/Carousel/Carousel.ts
	// hhds-image-text to ../components/ImageText/ImageText.ts
	if (!match) {
		match = path.match(/\.{1,2}\/blocks\/(?:core|feature)\/([^/]+)\/\1\.ts$/);
		if (match) {
			const kebabName = toKebabCase(match[1]);
			tagToLoaderMap[kebabName] = modules[path];
		}
	}

	if (!match) {
		DEBUG_VERBOSE && console.warn(LOG_PREFIX, `No matching tag found for path: ${path}`);
	}
}

const selector = Object.keys(tagToLoaderMap).join(',');

export const loadComponentCodeForTag = (tag: string) => {
	if (!tag) return;
	tag = tag.toLowerCase();
	if (customElements.get(tag)) {
		DEBUG_VERBOSE && console.log(LOG_PREFIX, `Component <${tag}> already defined, skipping load.`);
		return;
	}
	if (tagToLoaderMap[tag]) {
		DEBUG_VERBOSE && console.log(LOG_PREFIX, 'loadComponentCodeForTag:', tag);
		tagToLoaderMap[tag]?.()
			.then(() => DEBUG_VERBOSE && console.log(LOG_PREFIX, `Loaded component: <${tag}>`))
			.catch((err) => DEBUG_VERBOSE && console.error(LOG_PREFIX, `Failed to load <${tag}>:`, err));
	}
};

export const scanDomForComponentTags = (rootElement: Document | ShadowRoot | HTMLElement = document): Set<string> => {
	DEBUG_VERBOSE && console.log(LOG_PREFIX, 'scanDomForComponentTags');
	const componentKeys = Object.keys(componentModules).map((key) => key.split('/').pop()?.replace('.ts', ''));
	const kebabKeys = componentKeys.map((key) => `${prefix}-${toKebabCase(key || '')}`);
	// Find all elements in the DOM that use our prefix
	const viableElements = Array.from(rootElement.querySelectorAll('*')).filter((el) => el.tagName.toLowerCase().startsWith(prefix + '-'));

	// Find all component elements in the DOM
	const elements = viableElements.filter((el: Element) => kebabKeys.some((key) => key == el.tagName.toLowerCase()));

	// Get the names of those component elements
	const elementNames = elements.map((el: Element) => el.tagName.toLowerCase());

	// Remove duplicates
	const uniqueElementNames = new Set(elementNames);

	DEBUG_VERBOSE && console.log(LOG_PREFIX, 'Custom components in library:', kebabKeys.join(', '));
	DEBUG_VERBOSE && console.log(LOG_PREFIX, 'Custom elements found in DOM:', Array.from(uniqueElementNames).join(', '));

	return uniqueElementNames;
};

interface LoadRequiredModulesParams {
	pageElement?: HTMLElement;
	mimicPageEnter?: boolean;
	mimicPageEnterTransitionComplete?: boolean;
	onPageEnter?: boolean;
	onPageEnterInfo?: BlockLifecycleInfo;
}

type BlockSetupFunc = () => void;
interface BlockSetupInfo {
	func: BlockSetupFunc;
	blocktype: string;
}
type BlockSetupFuncMap = { [name: string]: BlockSetupInfo };

const loadRequiredModules = async (_logNote?: string, params: LoadRequiredModulesParams = {}) => {
	if (_logNote && DEBUG_VERBOSE) console.log(LOG_PREFIX, 'loadRequiredModules (' + _logNote + ')');
	// Load the component code for each unique element found in the DOM
	if (ENABLE_COMPONENT_AUTOLOADING) {
		scanDomForComponentTags().forEach((tag) => loadComponentCodeForTag(tag));
	}

	// Find all elements in the dom with an attribute named hhds-blocktype
	// and attempt to trigger their block handler code.
	const target = params.pageElement || document;
	const blockElements = Array.from(target.querySelectorAll<HTMLElement>(blocktypeQuery));

	const deferredBlockSetups: BlockSetupFuncMap | null = {};
	const blocktypePromises = blockElements.map((element) => processElementBlocktype(element, false, deferredBlockSetups));
	await Promise.all(blocktypePromises);
	// Iterate over the deferred setups by key to ensure order, and execute the setup functions.
	Object.keys(deferredBlockSetups).forEach((blockId) => deferredBlockSetups[blockId].func());
	if (DEBUG_VERBOSE) {
		console.log('Block order: ');
		Object.values(deferredBlockSetups).forEach((setupInfo, index) => {
			console.log(`${index + 1}. ${setupInfo.blocktype}`);
		});
	}

	isCms && convertClassnameAttributesForVisualEditor();

	if (params.mimicPageEnter) {
		blockLifecycleController.subscribe('module-loader', async (eventName: string, data?: any) => {
			// Once all blocks have dispatched their loaded event, we can trigger the mimicked
			// PageEnterTransitionComplete event. This is mimicked because initial page startup doesn't
			// use the Barba transition lifecycle, and our blocks are set up to perform particular
			// tasks under different lifecycle steps, with the occasional block waiting for transition
			// completion to do some setup, such as starting to play video.
			if (eventName == BlockEvent.BlocksFinalConfigComplete) {
				DEBUG_VERBOSE && console.log(LOG_PREFIX, 'AllBlocksFinalConfig');
				blockLifecycleController.unsubscribe('module-loader');
				if (params.mimicPageEnterTransitionComplete) blockLifecycleController.mimicPageEnterTransitionComplete();
			}
		});
		// Kick off the onPageEnter method on all blocks.
		blockLifecycleController.mimicPageEnter(target, null, !isCms, params.onPageEnterInfo);
	}
};

const processElementBlocktype = async (
	element: HTMLElement,
	firePageEnter: boolean = false,
	deferredBlockSetups: BlockSetupFuncMap | null = null
): Promise<void> => {
	const blockType: string | null = element.getAttribute('hhds-blocktype');
	//console.log(LOG_PREFIX, 'Processing block type:', blockType, 'for element:', element);
	if (!blockType) return;

	const loader: any = tagToLoaderMap[blockType];
	if (!loader) return;

	addBlockId(element);

	//console.log('{{LOADING}}: ', blockType);
	//await new Promise((resolve) => setTimeout(resolve, Math.random() * 800 + 200));

	return new Promise((resolve) => {
		loader()
			.then((module: any) => {
				//console.log('{{LOADED}}: ', blockType);
				//const blockHandler = result['handler'];
				const parent = element.parentElement;
				if (!parent) return;

				// In CMS, blocks seem to be re-initialised as the editor is starting up.
				// Ensure we don't set up more than one set of callbacks for a block.
				isCms && blockLifecycleController.clearBlockCallbacks(element);

				const blockSetupFunc = () => {
					blockLifecycleController.addBlockCallbacks(element, module);
					if (firePageEnter) {
						rosController.configureBlockRevealOnScroll();
						blockLifecycleController.mimicPageEnter(element, element, !isCms);
						blockLifecycleController.mimicPageEnterTransitionComplete(element);
					}
				};
				// If an BlockSetupFuncMap is provided, place the blockSetupFunc in it, so that setup can be executed
				// in sorted order after all modules are loaded. If this is not required, and no BlockSetupFuncMap is provided,
				// simply execute now.
				if (deferredBlockSetups) {
					const blockId = element.getAttribute('block-id');
					if (blockId) {
						deferredBlockSetups[blockId] = {
							func: blockSetupFunc,
							blocktype: blockType,
						};
					} else {
						console.log(LOG_PREFIX, 'Unable to defer block setup due to missing block-id attribute on element:', element);
					}
				} else {
					blockSetupFunc();
				}

				activeBlocks.add(element);
				resolve();
			})
			.catch((err: any) => {
				DEBUG_VERBOSE && console.error(LOG_PREFIX, `Failed to load block component for class ${blockType}:`, err);
				resolve();
			});
	});
};

//const postProcessBlockForCms = (element: HTMLElement) => {};

const isCms: boolean = document.body.classList.contains('wp-admin') || isIframeEditor();
blockLifecycleController.setCmsMode(isCms);
const activeBlocks: Set<HTMLElement> = new Set();

if (isIframeEditor()) {
	loadRequiredModules('iframe editor load', { mimicPageEnter: true, mimicPageEnterTransitionComplete: true });
	const observer = new MutationObserver((mutations) => {
		if (mutations.length > 0) {
			// For each active block, check if it's still in the DOM; if not, remove it from activeBlocks and delete its state.
			activeBlocks.forEach((block) => {
				const blockStillPresent = document.body.contains(block);
				if (!blockStillPresent) {
					activeBlocks.delete(block);
					blockLifecycleController.mimicPageLeave(block);
					blockLifecycleController.mimicPageLeaveComplete(block);
				}
			});
		}

		mutations.forEach((mutation) => {
			if (mutation.type === 'childList') {
				let mutationsProcessed: boolean = false;
				mutation.addedNodes.forEach((node) => {
					// The node will likely be a wp-block container, so check its children for hhds-blocktype elements.
					// At this point, a placeholder block could be in the DOM instead of one of ours, if ours emits
					// emitting no HTML, as a result of an empty-block checking logic.
					const htmlNode = node as HTMLElement;
					if (htmlNode && htmlNode.querySelectorAll) {
						// Not all nodes added here will belong to a custom block, but any block using
						// hhds-blocktype should trigger lifecycle setup.
						mutationsProcessed = true;
						htmlNode.querySelectorAll(blocktypeQuery).forEach((el) => {
							const firePageEnterEvents: boolean = true;
							if (firePageEnterEvents) rosController.configureBlockRevealOnScroll();
							processElementBlocktype(el as HTMLElement, firePageEnterEvents);
							//convertClassnameAttributes();
						});
					} else {
						//console.log('Node is invalid: ', node);
					}
				});
				if (mutationsProcessed) {
					convertClassnameAttributesForVisualEditor();
					if (ENABLE_COMPONENT_AUTOLOADING) {
						scanDomForComponentTags().forEach((tag) => loadComponentCodeForTag(tag));
					}
				}
			}
		});

		convertClassnameAttributesForVisualEditor();
	});
	observer.observe(document, { childList: true, subtree: true });
}

document.addEventListener(PageEvent.enter, (event: any) => {
	const incomingPage: HTMLElement = event?.detail?.next?.container;

	// When loading modules in response to a PageEvent.enter, we need to mimic
	// the enter event to the blocks whose code is only imported as a response to this.
	// For the PageEnterTransitionComplete event, we only need to mimic this if specified in the event detail,
	// which will be on hard page load, when we mimic Barba's page events by actually dispatching them manually.
	loadRequiredModules(PageEvent.enter, {
		pageElement: incomingPage,
		mimicPageEnter: true,
		mimicPageEnterTransitionComplete: event?.detail?.mimic === true,
		onPageEnter: true,
		onPageEnterInfo: { initialPage: event?.detail?.mimic === true, isCms },
	});
});

if (ENABLE_COMPONENT_AUTOLOADING) {
	const observer = new MutationObserver((mutations) => {
		for (const mutation of mutations) {
			for (const node of mutation.addedNodes) {
				if (node.nodeType !== Node.ELEMENT_NODE) continue;
				const el = node as HTMLElement;
				// Only run if the node itself is a relevant custom element or contains any
				if (el.tagName.toLowerCase().startsWith(prefix + '-') || el.querySelector(selector)) {
					loadComponentCodeForTag(el.tagName);
				}
			}
		}
	});

	observer.observe(document.body, { childList: true, subtree: true, attributes: false, characterData: false });
}

const shadowRootObserver = ENABLE_COMPONENT_AUTOLOADING
	? new MutationObserver((mutations) => {
			mutations.forEach((mutation) => {
				if (mutation.type != 'childList') return;
				mutation.addedNodes.forEach((node) => {
					if (node.nodeType === Node.ELEMENT_NODE) {
						const target = mutation.target as ShadowRoot;
						const parentComponent = target.host as Component;
						if (parentComponent) {
							const parentTag = parentComponent.tagName.toLowerCase();
							DEBUG_VERBOSE && console.log(LOG_PREFIX, `${parentTag} ShadowRoot mutation:`, node);
						}
						if (node instanceof HTMLElement) {
							node.querySelectorAll?.('*').forEach((el) => {
								// For each element in the shadow DOM, check for custom elements with our prefix
								const tag: string = el.tagName.toLowerCase();
								if (tag.startsWith(prefix + '-') && !customElements.get(tag)) loadComponentCodeForTag(tag);
							});
						}
						// Handle the case where the node itself is a custom element
						const tag: string = (node as HTMLElement).tagName.toLowerCase();
						if (!customElements.get(tag) && tag.startsWith(prefix + '-')) loadComponentCodeForTag(tag);
					}
				});
			});
		})
	: null;

// We can't just detect webcomponents by the presence of their shadowRoot property early in lifecycle,
// as the webcomponents are connected at the browser's discretion; we respond to this using an event.
// We could use a customElements.whenDefined() approach, but we can also use our own event.
document.addEventListener(ComponentEvent.CONNECTED, (event: any) => {
	const component: Component = event.detail as Component;
	if (ENABLE_COMPONENT_AUTOLOADING && component?.shadowRoot) {
		shadowRootObserver?.observe(component.shadowRoot, { childList: true, subtree: true });
	}
	if (isCms) loadRequiredModules('Component connected');
});
document.addEventListener('component-disconnected', (event: any) => {
	const component: Component = event.detail as Component;
	if (ENABLE_COMPONENT_AUTOLOADING && component?.shadowRoot) {
		shadowRootObserver?.disconnect();
	}
});

export function convertClassnameAttributesForVisualEditor() {
	// With the Gutenberg editor being housed within a React app, class attributes on
	// custom elements are converted to "classname", and we need to undo this to avoid
	// alternatives to 'class', which is especially difficult when using multiple, more
	// atomic classes on a single element.
	const classnameItems = document.querySelectorAll('[classname]');
	//if (classnameItems.length) console.log('Classname item: ', classnameItems);
	classnameItems.forEach((item) => {
		const classNameValue = item.getAttribute('classname');
		classNameValue && item.setAttribute('class', classNameValue);
	});
}
